import type {
  AdvancingShare,
  AdvancingSettings,
  AdvancingShareScope,
  ShowDTO,
  VenueDTO,
} from '../api';
import { pdfLocale } from './pdfI18n';

/**
 * buildAdvancingEmail — Phase D of Advancing-Spec.md.
 *
 * Generates the full body of a promoter advancing email as a copy-paste-ready
 * plain-text string (English only — hard site rule). It assembles:
 *   - a proposed subject line + greeting
 *   - the key show info (date, venue, city, band)
 *   - the per-scope advancing share links (Technical / Hotel / Logistics /
 *     Everything), each with its full URL `${origin}/share/advancing/<token>`
 *   - a short pointer to each category's detailed requests (tech / hotel /
 *     logistics) — the links carry the full per-item detail, so here we point
 *     the promoter to the right link rather than duplicate every rider line
 *   - a sign-off (thank-you + band name, plus the manager contact when known)
 *
 * Pure function: no API calls, no React. Data is passed in. Fields are all
 * optional so it degrades gracefully when the show/venue/band are incomplete.
 */

export interface AdvancingBandContact {
  name?: string;
  email?: string;
  phone?: string;
}

export interface AdvancingBandInfo {
  name?: string;
  /** Optional manager/promoter-facing contact used in the sign-off. */
  contact?: AdvancingBandContact;
  /** Public Promo kit link for this band (a token link — see Promo kit). When
   * present it is included in the email so the promoter can grab press kit /
   * logos / promo images. (2026-08-22) */
  promoKitUrl?: string;
}

const SCOPE_LABEL: Record<AdvancingShareScope, string> = {
  tech: 'Technical',
  hotel: 'Hotel / rooms',
  logistics: 'Logistics',
  all: 'Everything',
};

/** Pretty date like "Wed, 23 Sep 2026"; falls back to the raw string. */
function formatDate(value?: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  try {
    return d.toLocaleDateString(pdfLocale(), {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

function line(out: string[], text = ''): void {
  out.push(text);
}

export function buildAdvancingEmail(
  show: Partial<ShowDTO>,
  venue?: Partial<VenueDTO> | null,
  band?: AdvancingBandInfo | null,
  shares: AdvancingShare[] = [],
  settings?: AdvancingSettings | null,
  origin?: string,
): string {
  const baseOrigin =
    origin ?? (typeof window !== 'undefined' ? window.location.origin : '');
  const bandName = band?.name?.trim() || show.band_name?.trim() || '';
  const venueName = venue?.name?.trim() || show.venue_name?.trim() || '';
  const city = venue?.city?.trim() || show.city?.trim() || '';
  const dateLabel = formatDate(show.date);
  const venueLabel = [venueName, city].filter(Boolean).join(', ');

  const out: string[] = [];

  // ---- Subject + greeting ----
  line(out, `Subject: Advancing — ${bandName || 'the band'}${venueLabel ? ` @ ${venueLabel}` : ''}${dateLabel ? `, ${dateLabel}` : ''}`);
  line(out);
  line(out, 'Hi there,');
  line(out);

  // ---- Intro ----
  line(out, `We are reaching out ahead of our show${dateLabel ? ` on ${dateLabel}` : ''}${venueLabel ? ` at ${venueLabel}` : ''} to advance the production and logistics.`);
  line(out);

  // ---- Key show info ----
  line(out, 'SHOW DETAILS');
  if (bandName) line(out, `Band: ${bandName}`);
  if (venueLabel) line(out, `Venue: ${venueLabel}`);
  if (dateLabel) line(out, `Date: ${dateLabel}`);
  if (show.door_time) line(out, `Door time: ${show.door_time}`);
  if (show.timezone) line(out, `Timezone: ${show.timezone}`);
  if (show.electricity) line(out, `Electricity: ${show.electricity}`);
  line(out);

  // ---- Promo kit (2026-08-22) ----
  // A single public per-band link the promoter can use for press kit / logos /
  // promo images. Shown only when the band has a Promo kit token/link.
  if (band?.promoKitUrl) {
    line(out, 'PROMO KIT');
    line(out, 'For press materials, logos and promo images, you can find the band\'s Promo kit here:');
    line(out, band.promoKitUrl);
    line(out);
  }

  // ---- Share links ----
  const existing = shares.filter((s) => s && s.token);
  line(out, 'ADVANCING LINKS');
  line(out, 'Please confirm the items in each link below. Each link covers a specific area, handled by the right person on your side:');
  if (existing.length === 0) {
    line(out, '(No advancing share links have been created for this show yet.)');
  } else {
    for (const share of existing) {
      const label = SCOPE_LABEL[share.scope] || share.scope;
      const url = `${baseOrigin}/share/advancing/${share.token}`;
      line(out, `• ${label}: ${url}`);
    }
  }
  line(out);

  const hasTech = existing.some((s) => s.scope === 'tech' || s.scope === 'all');
  const hasHotel = existing.some((s) => s.scope === 'hotel' || s.scope === 'all');
  const hasLogistics = existing.some((s) => s.scope === 'logistics' || s.scope === 'all');

  const hotelBookedByBand = !!(settings && settings.hotel_booked_by_band);

  // ---- Technical ----
  if (hasTech) {
    line(out, 'TECHNICAL');
    const techLink = existing.find((s) => s.scope === 'tech' || s.scope === 'all');
    line(out, 'Our full technical requirements — sound, IEM, backline, lights and hospitality — are detailed on the Technical advancing link:');
    if (techLink) line(out, `${SCOPE_LABEL[techLink.scope]}: ${baseOrigin}/share/advancing/${techLink.token}`);
    line(out);
  }

  // ---- Hotel ----
  if (hasHotel && !hotelBookedByBand) {
    line(out, 'HOTEL / ACCOMMODATION');
    const hotelLink = existing.find((s) => s.scope === 'hotel' || s.scope === 'all');
    line(out, 'Please confirm the rooms and accommodation requests on the Hotel advancing link:');
    if (hotelLink) line(out, `${SCOPE_LABEL[hotelLink.scope]}: ${baseOrigin}/share/advancing/${hotelLink.token}`);
    line(out);
  } else if (hotelBookedByBand) {
    line(out, 'HOTEL / ACCOMMODATION');
    line(out, 'The band books its own hotel for this show — no room request needed from you.');
    line(out);
  }

  // ---- Logistics ----
  if (hasLogistics) {
    line(out, 'LOGISTICS');
    const logisticsLink = existing.find((s) => s.scope === 'logistics' || s.scope === 'all');
    const logisticsDetails: string[] = [];
    if (show.timezone) logisticsDetails.push(`Timezone: ${show.timezone}`);
    if (show.door_time) logisticsDetails.push(`Door time: ${show.door_time}`);
    if (show.luggage_notes) logisticsDetails.push(`Luggage: ${show.luggage_notes}`);
    if (logisticsDetails.length) {
      for (const d of logisticsDetails) line(out, `• ${d}`);
    }
    line(out, 'Please confirm the load-in, transport and any other logistics on the Logistics advancing link:');
    if (logisticsLink) line(out, `${SCOPE_LABEL[logisticsLink.scope]}: ${baseOrigin}/share/advancing/${logisticsLink.token}`);
    line(out);
  }

  // ---- Sign-off ----
  line(out, 'Thank you for your help with the advance — we look forward to the show!');
  if (band?.contact && (band.contact.name || band.contact.email || band.contact.phone)) {
    line(out);
    line(out, 'Best regards,');
    if (band.contact.name) line(out, band.contact.name);
    if (band.contact.email) line(out, band.contact.email);
    if (band.contact.phone) line(out, band.contact.phone);
  } else if (bandName) {
    line(out);
    line(out, 'Best regards,');
    line(out, bandName);
  }

  return out.join('\n');
}
